/**
 * Tabs Component V2
 *
 * WCAG 2.1 Level AA + BFSG konform
 * Built with Radix UI for accessibility
 *
 * Variants:
 * - underline: Classic underline tabs
 * - pills: Modern pill-style tabs (shadcnblocks-style)
 */

import { createContext, forwardRef, useContext, type ComponentPropsWithoutRef, type ElementRef } from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cn } from '@/lib/utils';

// ============================================================================
// Context for Variant
// ============================================================================

type TabsVariant = 'underline' | 'pills';

const TabsVariantContext = createContext<TabsVariant>('pills');

// ============================================================================
// Tabs Root
// ============================================================================

interface TabsProps extends ComponentPropsWithoutRef<typeof TabsPrimitive.Root> {
  variant?: TabsVariant;
}

const Tabs = forwardRef<ElementRef<typeof TabsPrimitive.Root>, TabsProps>(
  ({ variant = 'pills', ...props }, ref) => (
    <TabsVariantContext.Provider value={variant}>
      <TabsPrimitive.Root ref={ref} {...props} />
    </TabsVariantContext.Provider>
  )
);

Tabs.displayName = 'Tabs';

// ============================================================================
// Tabs List
// ============================================================================

const TabsList = forwardRef<
  ElementRef<typeof TabsPrimitive.List>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => {
  const variant = useContext(TabsVariantContext);

  return (
    <TabsPrimitive.List
      ref={ref}
      className={cn(
        'inline-flex items-center',
        // WCAG 2.1: Keyboard navigation
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',

        // Variant: Pills (shadcnblocks-style)
        variant === 'pills' && [
          'h-12 p-1 gap-1',
          'bg-neutral-100 dark:bg-neutral-800',
          'rounded-xl',
        ],

        // Variant: Underline (classic)
        variant === 'underline' && [
          'gap-1',
          'border-b border-neutral-200 dark:border-neutral-800',
          'pb-0',
        ],

        className
      )}
      {...props}
    />
  );
});

TabsList.displayName = TabsPrimitive.List.displayName;

// ============================================================================
// Tabs Trigger
// ============================================================================

const TabsTrigger = forwardRef<
  ElementRef<typeof TabsPrimitive.Trigger>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => {
  const variant = useContext(TabsVariantContext);

  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        'inline-flex items-center justify-center whitespace-nowrap',
        'text-sm font-medium',
        'transition-all duration-200',

        // BFSG: Touch targets minimum 44x44px
        'min-h-[44px]',

        // Focus state (WCAG 2.1: 3:1 contrast minimum)
        'focus-visible:outline-none',
        'focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-neutral-900',

        // Disabled state
        'disabled:pointer-events-none disabled:opacity-50',

        // Variant: Pills (shadcnblocks-style)
        variant === 'pills' && [
          'px-4 py-2.5 gap-2',
          'rounded-lg',
          'text-neutral-600 dark:text-neutral-400',
          'hover:text-neutral-900 dark:hover:text-neutral-100',
          'hover:bg-neutral-200/50 dark:hover:bg-neutral-700/50',
          // Active: White background with shadow
          'data-[state=active]:bg-white dark:data-[state=active]:bg-neutral-900',
          'data-[state=active]:text-neutral-900 dark:data-[state=active]:text-neutral-50',
          'data-[state=active]:shadow-sm',
        ],

        // Variant: Underline (classic)
        variant === 'underline' && [
          'px-4 py-3',
          'text-neutral-600 dark:text-neutral-400',
          'border-b-2 border-transparent',
          '-mb-px',
          'hover:text-neutral-900 dark:hover:text-neutral-100',
          'hover:border-neutral-300 dark:hover:border-neutral-700',
          'data-[state=active]:text-primary-700 dark:data-[state=active]:text-primary-400',
          'data-[state=active]:border-primary-600 dark:data-[state=active]:border-primary-400',
        ],

        className
      )}
      {...props}
    />
  );
});

TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

// ============================================================================
// Tabs Content
// ============================================================================

const TabsContent = forwardRef<
  ElementRef<typeof TabsPrimitive.Content>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'mt-4',
      // WCAG 2.1: Focus management
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
      // Animation
      'data-[state=active]:animate-in data-[state=active]:fade-in-0',
      className
    )}
    {...props}
  />
));

TabsContent.displayName = TabsPrimitive.Content.displayName;

// ============================================================================
// Exports
// ============================================================================

export { Tabs, TabsList, TabsTrigger, TabsContent };
export type { TabsProps, TabsVariant };
